import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { downloadAuthedFile, useAuthedBlobUrl } from "../api/authedFile";

interface ReportStatus {
  report_id: string;
  template_id: string;
  status: string;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
  file_size_bytes: number | null;
  error_message: string | null;
}

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();

  const status = useQuery({
    queryKey: ["report", id],
    queryFn: async () => (await api.get<ReportStatus>(`/reports/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => {
      const data = q.state.data;
      return data?.status === "COMPLETED" || data?.status === "FAILED" ? false : 2000;
    },
  });

  const isDone = status.data?.status === "COMPLETED";
  const isFail = status.data?.status === "FAILED";

  // De PDF wordt via de API-client opgehaald (bearer-token) en als object-URL
  // in de iframe gezet. Een directe `src` naar /preview krijgt geen header mee
  // en geeft 401 zodra de login aanstaat.
  const preview = useAuthedBlobUrl(isDone && id ? `/reports/${id}/preview` : null);
  const [bezig, setBezig] = useState(false);
  const [downloadFout, setDownloadFout] = useState<string | null>(null);

  if (!id) return null;

  const onDownload = async () => {
    setBezig(true);
    setDownloadFout(null);
    try {
      await downloadAuthedFile(`/reports/${id}/download`, `kbs-rapport-${id}.pdf`);
    } catch {
      setDownloadFout("Downloaden mislukt. Probeer het opnieuw.");
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/" className="text-xs text-kbs-accent hover:underline">
            ← Terug naar dashboard
          </Link>
          <h1 className="text-2xl font-bold text-kbs-primary">Rapport {id.slice(0, 8)}…</h1>
        </div>
        {isDone && (
          <button
            type="button"
            onClick={onDownload}
            disabled={bezig}
            className="rounded bg-kbs-primary px-4 py-2 text-sm font-semibold text-white hover:bg-kbs-dark disabled:opacity-60"
          >
            {bezig ? "Bezig…" : "Download PDF"}
          </button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Term label="Status">{status.data?.status ?? "…"}</Term>
          <Term label="Template">{status.data?.template_id ?? "—"}</Term>
          <Term label="Aangevraagd">
            {status.data ? new Date(status.data.requested_at).toLocaleString("nl-NL") : "—"}
          </Term>
          <Term label="Voltooid">
            {status.data?.completed_at
              ? new Date(status.data.completed_at).toLocaleString("nl-NL")
              : "—"}
          </Term>
          <Term label="Grootte">
            {status.data?.file_size_bytes
              ? `${Math.round(status.data.file_size_bytes / 1024)} KB`
              : "—"}
          </Term>
        </dl>
        {isFail && status.data?.error_message && (
          <div className="mt-4 rounded bg-rose-50 p-3 text-sm text-rose-700">
            <strong>Fout:</strong> {status.data.error_message}
          </div>
        )}
      </div>

      {downloadFout && (
        <div className="rounded bg-rose-50 p-3 text-sm text-rose-700">{downloadFout}</div>
      )}

      {isDone && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          {preview.error ? (
            <div className="p-8 text-center text-sm text-rose-700">{preview.error}</div>
          ) : preview.url ? (
            <iframe
              title="PDF preview"
              src={preview.url}
              className="h-[80vh] w-full"
              style={{ border: 0 }}
            />
          ) : (
            <div className="p-8 text-center text-sm text-slate-500">Rapport laden…</div>
          )}
        </div>
      )}
      {!isDone && !isFail && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
          Rapport wordt gegenereerd… deze pagina vernieuwt automatisch.
        </div>
      )}
    </div>
  );
}

function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-kbs-primary">{children}</dd>
    </div>
  );
}
